import { useCellulaProviderContext } from '@cellula/react';
import * as Portal from '@radix-ui/react-portal';
import { Fragment, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useScreenContext } from './use_screen.context';

interface Props {
	route: string;
	children: React.ReactNode | React.ReactNode[];
	canBeActivated?: boolean;
	isActivated?: boolean;
}

const StyledPortal = styled(Portal.Root)`
	display: flex;
	flex-direction: column;
	min-height: 100%;
	position: relative;
	top: 0;
	width: 100%;
	height: 100%;
`;

export function Screen({ children, canBeActivated = true, isActivated, route }: Props): JSX.Element | null {
	const { active, screens, protectedScreens, activate, dismiss } = useScreenContext();

	useEffect(
		function whenIsActivatedIsChanged() {
			if (isActivated) {
				screens.add(route);
				activate(route);
			}
		},
		[isActivated, screens, activate, route],
	);

	useEffect(
		function whenComponentUnmounts() {
			return () => {
				if (isActivated) {
					dismiss();
				}
			};
		},
		[isActivated, dismiss],
	);

	useEffect(
		function whenRoutePropIsChanged() {
			screens.add(route);

			return () => {
				screens.delete(route);
			};
		},
		[route, screens],
	);

	useEffect(
		function whenCanBeActivatedPropIsChanged() {
			if (!canBeActivated) {
				protectedScreens.add(route);
			} else {
				protectedScreens.delete(route);
			}

			return () => {
				protectedScreens.delete(route);
			};
		},
		[canBeActivated, protectedScreens, route],
	);

	if (active !== route) {
		return null;
	}

	if (canBeActivated) {
		return <ActivatedScreen>{children}</ActivatedScreen>;
	}

	return <Fragment>{children}</Fragment>;
}

interface ActivatedScreenProps {
	children: React.ReactNode | React.ReactNode[];
}

function ActivatedScreen({ children }: ActivatedScreenProps): JSX.Element {
	const ref = useRef<HTMLDivElement>((document.getElementById('root') ?? document.getElementById('app')) as HTMLDivElement);
	const cellulaContext = useCellulaProviderContext();
	const { target } = useScreenContext();

	return (
		<StyledPortal data-cellula-id={target != null ? cellulaContext.cellulaId : undefined} container={ref.current}>
			{children}
		</StyledPortal>
	);
}
